import React, { useState } from 'react';
import { Card, Button, Form, Row, Col, Table, Alert, Modal, Badge } from 'react-bootstrap';
import STPFormSelection from './STPFormSelection';

const STPPayroll = () => {
  const [payrollData, setPayrollData] = useState({
    abn: '',
    period_start: '',
    period_end: '',
    employees: []
  });
  const [newEmployee, setNewEmployee] = useState({
    employee_id: '',
    tfn: '',
    first_name: '',
    last_name: '',
    gross_pay: '',
    payg_withheld: '',
    super_liability: '',
    allowances: [],
    deductions: []
  });
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showFormSelection, setShowFormSelection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [selectedForms, setSelectedForms] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPayloadModal, setShowPayloadModal] = useState(false);
  const [payloadData, setPayloadData] = useState(null);
  const [isLoadingPayload, setIsLoadingPayload] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPayrollData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmployeeInputChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addEmployee = () => {
    if (newEmployee.employee_id && newEmployee.first_name && newEmployee.last_name) {
      if (editingEmployee !== null) {
        // Update existing employee
        setPayrollData(prev => ({
          ...prev,
          employees: prev.employees.map((emp, index) => 
            index === editingEmployee ? { ...newEmployee } : emp
          )
        }));
        setEditingEmployee(null);
      } else {
        // Add new employee
        setPayrollData(prev => ({
          ...prev,
          employees: [...prev.employees, { ...newEmployee }]
        }));
      }
      
      setNewEmployee({
        employee_id: '',
        tfn: '',
        first_name: '',
        last_name: '',
        gross_pay: '',
        payg_withheld: '',
        super_liability: '',
        allowances: [],
        deductions: []
      });
      setShowEmployeeModal(false);
    }
  };

  const editEmployee = (index) => {
    const employee = payrollData.employees[index];
    setNewEmployee({ ...employee });
    setEditingEmployee(index);
    setShowEmployeeModal(true);
  };

  const removeEmployee = (index) => {
    setPayrollData(prev => ({
      ...prev,
      employees: prev.employees.filter((_, i) => i !== index)
    }));
  };

  const handleSTPSubmission = async () => {
    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const response = await fetch('/api/stp/submit-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payrollData),
      });

      const result = await response.json();
      setSubmissionResult(result);
    } catch (error) {
      setSubmissionResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewSTPPayload = async () => {
    setIsLoadingPayload(true);
    setPayloadData(null);
    
    try {
      // First submit the event to get an ID
      const submitResponse = await fetch('/api/stp/submit-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payrollData),
      });

      if (submitResponse.ok) {
        const submitResult = await submitResponse.json();
        const eventId = submitResult.eventId;
        
        // Now get the payload preview
        const previewResponse = await fetch(`/api/stp/preview-payload/${eventId}`);
        
        if (previewResponse.ok) {
          const previewData = await previewResponse.json();
          setPayloadData(previewData);
          setShowPayloadModal(true);
        } else {
          const errorData = await previewResponse.json();
          alert(`Error previewing payload: ${errorData.error}`);
        }
      } else {
        const errorData = await submitResponse.json();
        alert(`Error creating event: ${errorData.error}`);
      }
    } catch (error) {
      alert(`Error previewing payload: ${error.message}`);
    } finally {
      setIsLoadingPayload(false);
    }
  };

  const generateSTPSummary = async () => {
    try {
      const response = await fetch('/api/stp/generate-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payrollData),
      });

      if (response.ok) {
        // Handle file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `STP-Event-${payrollData.abn}-${payrollData.period_start}-${payrollData.period_end}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const error = await response.json();
        alert('Error generating STP summary: ' + error.error);
      }
    } catch (error) {
      alert('Error generating STP summary: ' + error.message);
    }
  };

  const calculateTotals = () => {
    const totalGross = payrollData.employees.reduce((sum, emp) => sum + (parseFloat(emp.gross_pay) || 0), 0);
    const totalPAYG = payrollData.employees.reduce((sum, emp) => sum + (parseFloat(emp.payg_withheld) || 0), 0);
    const totalSuper = payrollData.employees.reduce((sum, emp) => sum + (parseFloat(emp.super_liability) || 0), 0);

    return { totalGross, totalPAYG, totalSuper };
  };

  const totals = calculateTotals();

  const handleFormSelection = (forms) => {
    setSelectedForms(forms);
    if (forms.length > 0) {
      // Pre-populate with data from the first form
      const firstForm = forms[0];
      setPayrollData(prev => ({
        ...prev,
        abn: firstForm.abn,
        period_start: firstForm.period_start,
        period_end: firstForm.period_end
      }));
    }
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-users me-2"></i>
        Single Touch Payroll (STP) Management
      </h3>

      {/* Form Selection Section */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>BAS Forms for STP Submission</span>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowFormSelection(true)}
          >
            <i className="fas fa-list-check me-1"></i>
            Select Forms
          </Button>
        </Card.Header>
        <Card.Body>
          {selectedForms.length === 0 ? (
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              No forms selected. Click "Select Forms" to choose validated BAS forms for STP submission.
            </Alert>
          ) : (
            <div>
              <h6>Selected Forms ({selectedForms.length}):</h6>
              <div className="table-responsive">
                <Table striped hover size="sm">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>ABN</th>
                      <th>Period</th>
                      <th>Total Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedForms.map((form) => (
                      <tr key={form.id}>
                        <td>{form.business_name}</td>
                        <td>{form.abn}</td>
                        <td>{form.period_start} to {form.period_end}</td>
                        <td className="text-end">${parseFloat(form.total_amount || 0).toFixed(2)}</td>
                        <td>
                          <Badge bg="success">Ready</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Business Information */}
      <Card className="mb-4">
        <Card.Header>Business Information</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>ABN *</Form.Label>
                <Form.Control
                  type="text"
                  name="abn"
                  value={payrollData.abn}
                  onChange={handleInputChange}
                  placeholder="11 digit ABN"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Period Start *</Form.Label>
                <Form.Control
                  type="date"
                  name="period_start"
                  value={payrollData.period_start}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Period End *</Form.Label>
                <Form.Control
                  type="date"
                  name="period_end"
                  value={payrollData.period_end}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Employee Management */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Employees ({payrollData.employees.length})</span>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowEmployeeModal(true)}
          >
            <i className="fas fa-plus me-1"></i>
            Add Employee
          </Button>
        </Card.Header>
        <Card.Body>
          {payrollData.employees.length === 0 ? (
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              No employees added yet. Click "Add Employee" to get started.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <th>TFN</th>
                    <th>Gross Pay</th>
                    <th>PAYG Withheld</th>
                    <th>Super Liability</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payrollData.employees.map((employee, index) => (
                    <tr key={index}>
                      <td>{employee.employee_id}</td>
                      <td>{employee.first_name} {employee.last_name}</td>
                      <td>{employee.tfn}</td>
                      <td>${parseFloat(employee.gross_pay || 0).toFixed(2)}</td>
                      <td>${parseFloat(employee.payg_withheld || 0).toFixed(2)}</td>
                      <td>${parseFloat(employee.super_liability || 0).toFixed(2)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => editEmployee(index)}
                          className="me-2"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeEmployee(index)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Payroll Totals */}
      <Card className="mb-4">
        <Card.Header>Payroll Totals</Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="text-center">
                <h5 className="text-primary">${totals.totalGross.toFixed(2)}</h5>
                <small className="text-muted">Total Gross Pay</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h5 className="text-warning">${totals.totalPAYG.toFixed(2)}</h5>
                <small className="text-muted">Total PAYG Withheld</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h5 className="text-success">${totals.totalSuper.toFixed(2)}</h5>
                <small className="text-muted">Total Super Liability</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Submission Result */}
      {submissionResult && (
        <Alert variant={submissionResult.success ? 'success' : 'danger'} className="mb-4">
          <i className={`fas fa-${submissionResult.success ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
          <strong>
            {submissionResult.success ? 'STP Event Submitted Successfully!' : 'STP Submission Failed'}
          </strong>
          {submissionResult.success && submissionResult.eventId && (
            <div className="mt-2">
              <strong>Event ID:</strong> {submissionResult.eventId}
            </div>
          )}
          {submissionResult.error && (
            <div className="mt-2">
              <strong>Error:</strong> {submissionResult.error}
            </div>
          )}
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between">
        <div>
          <Button
            variant="outline-warning"
            onClick={previewSTPPayload}
            disabled={isLoadingPayload || payrollData.employees.length === 0}
            className="me-2"
          >
            {isLoadingPayload ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                Loading...
              </>
            ) : (
              <>
                <i className="fas fa-eye me-1"></i>
                Preview Payload
              </>
            )}
          </Button>
          <Button
            variant="outline-info"
            onClick={generateSTPSummary}
            disabled={payrollData.employees.length === 0}
            className="me-2"
          >
            <i className="fas fa-file-pdf me-1"></i>
            Generate Summary
          </Button>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={handleSTPSubmission}
            disabled={isSubmitting || payrollData.employees.length === 0}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin me-1"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-1"></i>
                Submit STP Event
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Add/Edit Employee Modal */}
      <Modal show={showEmployeeModal} onHide={() => setShowEmployeeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingEmployee !== null ? 'Edit Employee' : 'Add Employee'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employee ID *</Form.Label>
                <Form.Control
                  type="text"
                  name="employee_id"
                  value={newEmployee.employee_id}
                  onChange={handleEmployeeInputChange}
                  placeholder="Employee ID"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>TFN *</Form.Label>
                <Form.Control
                  type="text"
                  name="tfn"
                  value={newEmployee.tfn}
                  onChange={handleEmployeeInputChange}
                  placeholder="9 digit TFN"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="first_name"
                  value={newEmployee.first_name}
                  onChange={handleEmployeeInputChange}
                  placeholder="First Name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="last_name"
                  value={newEmployee.last_name}
                  onChange={handleEmployeeInputChange}
                  placeholder="Last Name"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Gross Pay *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="gross_pay"
                  value={newEmployee.gross_pay}
                  onChange={handleEmployeeInputChange}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>PAYG Withheld</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="payg_withheld"
                  value={newEmployee.payg_withheld}
                  onChange={handleEmployeeInputChange}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Super Liability</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="super_liability"
                  value={newEmployee.super_liability}
                  onChange={handleEmployeeInputChange}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowEmployeeModal(false);
            setEditingEmployee(null);
            setNewEmployee({
              employee_id: '',
              tfn: '',
              first_name: '',
              last_name: '',
              gross_pay: '',
              payg_withheld: '',
              super_liability: '',
              allowances: [],
              deductions: []
            });
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={addEmployee}>
            {editingEmployee !== null ? 'Update Employee' : 'Add Employee'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Form Selection Modal */}
      {showFormSelection && (
        <STPFormSelection
          onFormSelect={handleFormSelection}
          onClose={() => setShowFormSelection(false)}
        />
      )}

      {/* STP Payload Preview Modal */}
      <Modal show={showPayloadModal} onHide={() => setShowPayloadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-eye me-2"></i>
            STP Payload Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {payloadData && (
            <div>
              {/* Summary Statistics */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Summary Statistics</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <strong>Total Employees:</strong><br />
                      <span className="text-primary fs-5">{payloadData.preview.totalEmployees}</span>
                    </Col>
                    <Col md={3}>
                      <strong>Total Gross Pay:</strong><br />
                      <span className="text-success fs-5">${payloadData.preview.totalGross.toFixed(2)}</span>
                    </Col>
                    <Col md={3}>
                      <strong>Total Tax Withheld:</strong><br />
                      <span className="text-info fs-5">${payloadData.preview.totalTax.toFixed(2)}</span>
                    </Col>
                    <Col md={3}>
                      <strong>Submission Type:</strong><br />
                      <Badge bg="secondary">{payloadData.preview.submissionType}</Badge>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <strong>Period:</strong> {payloadData.preview.periodStart} to {payloadData.preview.periodEnd}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Raw JSON Payload */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Raw JSON Payload</h6>
                </Card.Header>
                <Card.Body>
                  <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {JSON.stringify(payloadData.payload, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayloadModal(false)}>
            Close
          </Button>
          <Button variant="info" onClick={() => {
            if (payloadData) {
              const blob = new Blob([JSON.stringify(payloadData.payload, null, 2)], { type: 'application/json' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `STP-Payload-${payrollData.abn}-${payrollData.period_start}.json`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }
          }}>
            <i className="fas fa-download me-1"></i>
            Download JSON
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default STPPayroll;
